import React, { useState } from 'react';
import { Alert, Button } from 'antd';
import { FiDownload } from 'react-icons/fi';
import toast from 'react-hot-toast';
import AppRangePicker from '@/components/common/AppRangePicker';
import ifaService from '../../../services/ifaService';
import {
  buildPerformanceReportFilename,
  getDefaultPerformanceReportRange,
  toPerformanceReportPayload,
  triggerPdfDownload,
} from '../commons/partnerPerformanceReport.helpers';

export default function PartnerPerformanceReportTab({ partnerId, partnerName }) {
  const [dateRange, setDateRange] = useState(getDefaultPerformanceReportRange);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!partnerId || !dateRange?.[0] || !dateRange?.[1]) {
      toast.error('Select a valid date range.');
      return;
    }

    const payload = toPerformanceReportPayload(dateRange);
    setGenerating(true);
    setError('');

    try {
      const blob = await ifaService.generatePartnerPerformanceReport(partnerId, payload);
      const filename = buildPerformanceReportFilename(
        partnerName,
        payload.start_date,
        payload.end_date,
      );
      triggerPdfDownload(blob, filename);
      toast.success('Performance report downloaded.');
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        err?.message ||
        'Failed to generate performance report.';
      setError(message);
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5 pt-2">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Performance Report</h3>
        <p className="text-sm text-gray-500 mt-1">
          Generate a branded PDF business performance report for this partner based on fulfilled
          Pre-IPO orders in the selected period.
        </p>
      </div>

      {error ? <Alert type="error" message={error} showIcon /> : null}

      <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
            Report period
          </p>
          <AppRangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates?.[0] && dates?.[1]) setDateRange(dates);
            }}
            allowClear={false}
            format="DD MMM YYYY"
          />
        </div>
        <Button
          type="primary"
          icon={<FiDownload />}
          loading={generating}
          onClick={handleGenerate}
        >
          Generate report
        </Button>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-4 text-sm text-gray-600">
        <p className="font-semibold text-gray-800 mb-2">Report includes</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Cover page with partner name and date range</li>
          <li>Summary metrics and AI-generated key insights</li>
          <li>Monthly business performance overview</li>
          <li>Share-wise order distribution</li>
        </ul>
      </div>
    </div>
  );
}
